/**
 * Compound interest calculations.
 * Supports lump sum, recurring (DPS-style) contributions, multiple compounding
 * frequencies (including continuous), and produces a year-by-year schedule.
 */

export type CompoundFrequency =
  | "annually"
  | "semiannually"
  | "quarterly"
  | "monthly"
  | "daily"
  | "continuously";

export type CalcMode = "lumpsum" | "recurring" | "both";

export type ContribTiming = "begin" | "end"; // annuity-due vs annuity-ordinary

export interface CompoundInput {
  principal: number;
  monthlyContribution: number; // 0 if lumpsum-only
  annualRatePct: number; // e.g. 8 for 8%
  years: number; // total time period (can be fractional)
  frequency: CompoundFrequency;
  contribTiming?: ContribTiming;
}

export interface YearRow {
  year: number; // 1..ceil(years)
  opening: number;
  contributions: number;
  interest: number;
  closing: number;
}

export interface CompoundResult {
  maturity: number;
  totalInvested: number;
  totalInterest: number;
  wealthMultiplier: number; // maturity / totalInvested (or maturity/principal if no contrib)
  yearly: YearRow[];
}

export function periodsPerYear(freq: CompoundFrequency): number {
  switch (freq) {
    case "annually":
      return 1;
    case "semiannually":
      return 2;
    case "quarterly":
      return 4;
    case "monthly":
      return 12;
    case "daily":
      return 365;
    case "continuously":
      return Infinity;
  }
}

/**
 * Closed-form lump-sum compound amount.
 * A = P (1 + r/n)^(n t), or P e^(r t) for continuous.
 */
export function lumpSumAmount(principal: number, annualRatePct: number, years: number, freq: CompoundFrequency): number {
  if (principal <= 0 || years <= 0) return Math.max(0, principal);
  const r = annualRatePct / 100;
  if (freq === "continuously") return principal * Math.exp(r * years);
  const n = periodsPerYear(freq);
  return principal * Math.pow(1 + r / n, n * years);
}

/**
 * Future value of a stream of monthly contributions.
 * Contributions occur monthly; interest is compounded at the chosen frequency.
 * We simulate month-by-month so all frequency/timing combinations are exact.
 */
function recurringAmount(
  monthlyContribution: number,
  annualRatePct: number,
  years: number,
  freq: CompoundFrequency,
  timing: ContribTiming,
): number {
  if (monthlyContribution <= 0 || years <= 0) return 0;
  const r = annualRatePct / 100;
  const months = Math.round(years * 12);
  // Compounds-per-year, expressed as a per-month growth factor.
  // For continuous: factor over 1 month is e^(r/12). Otherwise compound n times/year => per-month = (1 + r/n)^(n/12)
  let monthlyGrowth: number;
  if (freq === "continuously") monthlyGrowth = Math.exp(r / 12);
  else {
    const n = periodsPerYear(freq);
    monthlyGrowth = Math.pow(1 + r / n, n / 12);
  }
  let bal = 0;
  for (let m = 0; m < months; m++) {
    if (timing === "begin") {
      bal = (bal + monthlyContribution) * monthlyGrowth;
    } else {
      bal = bal * monthlyGrowth + monthlyContribution;
    }
  }
  return bal;
}

/**
 * Year-by-year simulation that handles lump sum + monthly contribution together.
 */
function yearlySchedule(input: CompoundInput): YearRow[] {
  const { principal, monthlyContribution, annualRatePct, years, frequency } = input;
  const timing: ContribTiming = input.contribTiming ?? "begin";
  const r = annualRatePct / 100;
  const totalMonths = Math.max(0, Math.round(years * 12));
  const monthlyGrowth =
    frequency === "continuously"
      ? Math.exp(r / 12)
      : Math.pow(1 + r / periodsPerYear(frequency), periodsPerYear(frequency) / 12);

  const rows: YearRow[] = [];
  let bal = principal;
  let m = 0;
  let yearIdx = 1;
  while (m < totalMonths) {
    const opening = bal;
    let contribThisYear = 0;
    let interestThisYear = 0;
    const target = Math.min(totalMonths, m + 12);
    while (m < target) {
      const before = bal;
      if (monthlyContribution > 0 && timing === "begin") {
        bal += monthlyContribution;
        contribThisYear += monthlyContribution;
      }
      const grown = bal * monthlyGrowth;
      const interestStep = grown - bal;
      bal = grown;
      interestThisYear += interestStep;
      if (monthlyContribution > 0 && timing === "end") {
        bal += monthlyContribution;
        contribThisYear += monthlyContribution;
      }
      void before;
      m++;
    }
    rows.push({
      year: yearIdx,
      opening: round2(opening),
      contributions: round2(contribThisYear),
      interest: round2(interestThisYear),
      closing: round2(bal),
    });
    yearIdx++;
  }

  // Edge: years === 0 -> still emit the principal as year 0 if needed
  if (rows.length === 0 && principal > 0) {
    rows.push({ year: 1, opening: principal, contributions: 0, interest: 0, closing: principal });
  }
  return rows;
}

export function computeCompound(input: CompoundInput): CompoundResult {
  const timing: ContribTiming = input.contribTiming ?? "begin";
  const lump = lumpSumAmount(input.principal, input.annualRatePct, input.years, input.frequency);
  const recur = recurringAmount(input.monthlyContribution, input.annualRatePct, input.years, input.frequency, timing);
  const maturity = lump + recur;
  const months = Math.round(input.years * 12);
  const totalInvested = input.principal + input.monthlyContribution * months;
  const totalInterest = maturity - totalInvested;
  const denom = totalInvested > 0 ? totalInvested : 1;
  const wealthMultiplier = totalInvested > 0 ? maturity / denom : 0;
  const yearly = yearlySchedule(input);
  return {
    maturity: round2(maturity),
    totalInvested: round2(totalInvested),
    totalInterest: round2(totalInterest),
    wealthMultiplier,
    yearly,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function scheduleToCSV(rows: YearRow[]): string {
  const head = "Year,Opening,Contributions,Interest,Closing";
  const body = rows
    .map((r) => [r.year, r.opening, r.contributions, r.interest, r.closing].join(","))
    .join("\n");
  return head + "\n" + body + "\n";
}
